import * as React from 'react';
import { useRef, useState } from 'react';

import cn from 'classnames';
import { format as timeagoFormat } from 'timeago.js';

import { api } from '@api/reduxApi';
import { Dropdown, DropdownLink, InfoLabel, Text } from '@components/common';
import { AvatarFromId } from '@components/common/Avatar';
import { PageTitle } from '@components/common/helmets';
import { ArtifactBreadcrumbs } from '@components/RepositoryApp/components/ArtifactBreadcrumbs';
import { CopyURLButton } from '@components/shared/CopyURLButton';
import { EditableHeader } from '@components/shared/EditableHeader';
import { PDFView } from '@components/shared/PDFView';
import { InsightShareAccessDropdown } from '@components/shared/ShareAccess/InsightShareAccessDropdown';
import { Views } from '@components/shared/Views';
import { BubbleKebabSVG, DownloadSVG, PdfSVG, TrashSVG } from '@components/svgs';
import { TagList } from '@components/tags';
import { track } from '@components/tracking';
import { usePermission } from '@hooks/usePermission';
import { useResizeObserver } from '@hooks/useResizeObserver';
import { useToaster } from '@stores/toaster';

import * as toasts from '../toasts';

import { InsightEditor } from './components';
import { DeleteInsightModal } from './DeleteInsightModal';

interface Props {
  insight: Insight;
}

export const InsightPage: React.FC<React.PropsWithChildren<Props>> = ({ insight }) => {
  const { type = 'insight', created_at, updated_at } = insight;

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const width = useResizeObserver(wrapperRef);

  const showToast = useToaster();

  const canEdit = usePermission<Insight>('updateInsight')(insight);
  const canDelete = usePermission<Insight>('deleteInsight')(insight);

  const [updateInsight, { isLoading }] = api.useUpdateInsightMutation();

  const handleTitleOrDescChanged = async (mode: 'title' | 'description', value: string) => {
    try {
      setSaving?.(true);
      track(`updated_story_${mode}`, { story_id: insight.id });
      await updateInsight({ slug: insight.slug, [mode]: value }).unwrap();
    } catch {
      showToast(toasts.failedUpdate());
    } finally {
      setSaving?.(false);
    }
  };

  return (
    <div className={cn('mx-auto flex flex-col', { 'desktop:h-screen h-screen-w-navbar': type === 'insight' })}>
      <PageTitle>{insight.title || 'New Insight'}</PageTitle>
      <header className='desktop:px-12 border-b border-gray-200 bg-white px-4 pb-2 pt-4'>
        <ArtifactBreadcrumbs artifactTitle={insight.title || 'New Insight'} study={insight.study} />

        <div className='tablet:flex mb-1 items-start'>
          {canEdit ? (
            <div className='flex-grow'>
              <EditableHeader
                className='w-full truncate'
                placeholder='Insight title...'
                initialValue={insight.title}
                onBlur={(value) => handleTitleOrDescChanged('title', value)}
              />
              <EditableHeader
                mode='description'
                className='mt-4 w-full truncate'
                placeholder='Add description'
                initialValue={insight.description}
                onBlur={(value) => handleTitleOrDescChanged('description', value)}
              />
            </div>
          ) : (
            <div className='flex-grow'>
              <Text h='600' bold>
                {insight.title || 'Untitled insight'}
              </Text>

              {insight.description ? (
                <Text h='400' bold className='mt-4 w-full'>
                  {insight.description}
                </Text>
              ) : null}
            </div>
          )}

          <div className='tablet:ml-10 tablet:justify-end flex items-center space-x-2'>
            <AvatarFromId size='xl' userId={insight.creator_id} />
            <CopyURLButton icon='link' text={window.location.href} />
            {insight.views && <Views views={insight.views} />}
            <InsightShareAccessDropdown insight={insight} readOnly={!canEdit} />
            {canDelete && (
              <Dropdown
                onClick={() => setDropdownOpen(!dropdownOpen)}
                onClose={() => setDropdownOpen(false)}
                isOpen={dropdownOpen}
                hideCaret
                textStyle
                text={<BubbleKebabSVG />}
              >
                {type === 'file' && (
                  <DropdownLink className='flex items-center space-x-2'>
                    <DownloadSVG className='' />
                    <span>Download PDF</span>
                  </DropdownLink>
                )}
                <DropdownLink
                  onClick={() => {
                    setDeleteModalOpen(true);
                    setDropdownOpen(false);
                  }}
                  className='flex items-center space-x-2'
                >
                  <TrashSVG className='text-red-500' />
                  <span className='text-red-500'>Delete insight…</span>
                </DropdownLink>
              </Dropdown>
            )}
            <DeleteInsightModal
              open={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              slug={insight.slug}
              title={insight.title}
            />
          </div>
        </div>

        <div className='w-full'>
          <div className='flex flex-wrap'>
            <InfoLabel
              className='mr-4 mt-2'
              icon='lightBulb'
              text='Insight'
              suffix={type === 'file' ? <PdfSVG className='h-4 w-4' /> : null}
            />
            <InfoLabel
              className='mr-4 mt-2'
              pill={false}
              icon='checkmark'
              text={`Created ${timeagoFormat(created_at)}`}
            />

            <InfoLabel
              className='mr-4 mt-2'
              pill={false}
              icon='checkmark'
              text={`Updated ${timeagoFormat(updated_at)}`}
            />

            <div className='max-w-sm'>
              <TagList
                studyId={insight.project_id}
                tagIds={insight.tag_ids}
                readOnly={!canEdit}
                onChange={(tag_ids) => {
                  updateInsight({ ...insight, tag_ids });
                  track('saved_tags_to_story', { story_id: insight.id });
                }}
                className='focus:outline-none mt-2 flex h-10 w-full items-center space-x-2 rounded px-4 hover:bg-gray-50 focus:bg-gray-50'
                placement='bottom'
              />
            </div>
          </div>
        </div>
      </header>
      {type === 'insight' ? (
        <InsightEditor insight={insight} setSaving={setSaving} loading={isLoading || saving} />
      ) : (
        <div className='px-page py-gutter flex-1'>
          <div ref={wrapperRef} className='mx-auto h-full min-h-0 w-full max-w-6xl bg-white'>
            <PDFView width={width} url={insight.url || ''} />
          </div>
        </div>
      )}
    </div>
  );
};
